import { gql, useApolloClient, useMutation } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Avatar,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Group,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import React, { FormEvent, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import {
  graphRouteConfig,
  graphSettingsRouteConfig,
} from 'src/app/graph/routes';
import { Form } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useOrgCreationLocation } from 'src/hooks/useOrgCreationLocation/useOrgCreationLocation';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

const organizationsCardSettingsCommonFragment = gql`
  fragment OrganizationsCardSettingsCommonFragment on User {
    id
    type
    memberships {
      role: permission
      account {
        id
        avatarUrl
        name
        sso {
          provider
        }
        services {
          id
          title
          roleOverrides {
            user {
              id
            }
            role
          }
          variants {
            id
            name
          }
        }
        memberships {
          role: permission
          account {
            id
          }
          user {
            id
          }
        }
      }
      user {
        id
      }
    }
  }
`;

export const OrganizationsCardSettingsFragment = gql`
  fragment OrganizationsCardSettingsFragment on User {
    ...OrganizationsCardSettingsCommonFragment
    id
    memberships {
      account {
        id
        currentPlan {
          id
          userRoles
        }
      }
      user {
        id
      }
    }
  }
  ${organizationsCardSettingsCommonFragment}
`;

const LeaveOrganizationMutation = gql<
  GraphQLTypes.User__SettingsPageLeaveOrganizationMutation,
  GraphQLTypes.User__SettingsPageLeaveOrganizationMutationVariables
>`
  mutation User__SettingsPageLeaveOrganizationMutation(
    $accountId: ID!
    $userId: ID!
  ) {
    account(id: $accountId) {
      removeMember(id: $userId) {
        id
        memberships {
          account {
            id
          }
          user {
            id
          }
        }
      }
    }
  }
`;

interface Props {
  user: GraphQLTypes.OrganizationsCardSettingsFragment;
}
export function OrganizationsCard({
  user: { type, id: userId, memberships: organizations },
}: Props) {
  const client = useApolloClient();
  const [selectedOrg, setSelectedOrg] =
    useState<GraphQLTypes.OrganizationsCardSettingsFragment_memberships | null>(
      null,
    );
  const [error, setError] = useState<string | null>(null);
  const onModalClose = () => {
    setError(null);
    setSelectedOrg(null);
  };
  const newOrgLocation = useOrgCreationLocation();

  const [leaveOrganizationMutation, { loading }] = useMutation(
    LeaveOrganizationMutation,
    {
      variables: { accountId: selectedOrg?.account.id ?? '', userId },
      onCompleted: () => {
        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'me',
        });
        // Remove all dangling references to force refetches of all this data.
        client.cache.gc();

        onModalClose();
      },
      onError: (e) => setError(e.message),
    },
  );

  const submit = (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    setError(null);
    leaveOrganizationMutation();
  };

  const isLastAdminInSelectedAccount =
    selectedOrg?.role &&
    selectedOrg.role === GraphQLTypes.UserPermission.ORG_ADMIN &&
    selectedOrg.account.memberships?.filter(
      ({ role }) => role === GraphQLTypes.UserPermission.ORG_ADMIN,
    ).length === 1;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Organizations</CardTitle>
          <Tooltip
            label="You can't make a new organization with this user account because this account is managed with your current organization's SSO."
            disabled={type !== GraphQLTypes.UserType.SSO}
          >
            <Button
              as={ReactRouterLink}
              to={newOrgLocation}
              variant="secondary"
              isDisabled={type === GraphQLTypes.UserType.SSO}
            >
              New organization
            </Button>
          </Tooltip>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-[35%]">Organization</Th>
                  <Th className="w-[10%]">
                    <Group gap={2}>
                      Role
                      <Tooltip
                        interactive
                        label={
                          <>
                            You can read about the different roles in the{' '}
                            <Link to={DocsRouter.path('UserRoles')} isExternal>
                              Studio documentation.
                            </Link>
                          </>
                        }
                      >
                        <IconInfo className="text-icon-primary" />
                      </Tooltip>
                    </Group>
                  </Th>
                  <Th className="w-[20%]">
                    <Group gap={2}>
                      Graph roles
                      <Tooltip
                        interactive
                        label={
                          organizations.every(
                            ({ account }) => !account.currentPlan?.userRoles,
                          ) ? (
                            <>
                              Graph roles are a new feature that is currently
                              available by request. Please{' '}
                              <ContactSupportLink>
                                contact support
                              </ContactSupportLink>{' '}
                              to request access.
                            </>
                          ) : (
                            <>
                              This column contains any graphs where you have a
                              role override. You can read about the different
                              roles in the{' '}
                              <Link
                                isExternal
                                to={DocsRouter.path('GraphRoles')}
                              >
                                Studio documentation.
                              </Link>
                            </>
                          )
                        }
                      >
                        <IconInfo className="text-icon-primary" />
                      </Tooltip>
                    </Group>
                  </Th>
                  <Th className="w-[5%]">
                    <span className="sr-only">Actions</span>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {organizations.map((membership) => {
                  const {
                    role,
                    account: { avatarUrl, name, id, services },
                  } = membership;

                  const graphRoleOverrides = services.flatMap((graph) => {
                    const roleOverride = graph.roleOverrides?.find(
                      ({ user }) => user.id === userId,
                    );
                    return roleOverride
                      ? [{ graph, role: roleOverride.role }]
                      : [];
                  });

                  return (
                    <Tr key={membership.account.id}>
                      <Td>
                        <Group gap={2}>
                          <Avatar
                            size="xs"
                            name={name}
                            src={avatarUrl ?? undefined}
                            variant="org"
                          />
                          <Link to={orgRoutes.graphs.location({ orgId: id })}>
                            {name}
                          </Link>
                        </Group>
                      </Td>
                      <Td>
                        <div className="truncate">
                          {Config.roleNameByRoleEnum[role]}
                        </div>
                      </Td>
                      <Td>
                        <div className="text-base">
                          {graphRoleOverrides.length > 0 && (
                            <Tooltip
                              interactive
                              placement="bottom-start"
                              label={graphRoleOverrides.map(
                                ({ role: roleOverride, graph }) => {
                                  return graph.variants[0]?.name ? (
                                    <div
                                      key={`${graph.title} (${Config.roleNameByRoleEnum[roleOverride]})`}
                                    >
                                      <PermissionGuard
                                        graphId={graph.id}
                                        // Match permissions for accessPage
                                        graphPermissions={[
                                          'canQueryTokens',
                                          'canUpdateTitle',
                                          'canUpdateAvatar',
                                        ]}
                                        fallback={
                                          <Link
                                            to={graphRouteConfig.location({
                                              graphId: graph.id,
                                            })}
                                          >
                                            {graph.title}
                                          </Link>
                                        }
                                      >
                                        <Link
                                          to={graphSettingsRouteConfig.location(
                                            {
                                              graphId: graph.id,
                                              tab: 'permissions',
                                              // this is temporary, we can remove
                                              // the reliance on variant here once
                                              // we move graph settings to be on
                                              // the full graph route
                                              graphVariant:
                                                graph.variants[0].name,
                                            },
                                          )}
                                        >
                                          {graph.title}
                                        </Link>
                                      </PermissionGuard>{' '}
                                      ({Config.roleNameByRoleEnum[roleOverride]}
                                      )
                                    </div>
                                  ) : null;
                                },
                              )}
                            >
                              <div>
                                {graphRoleOverrides.length} role{' '}
                                {format.pluralize(
                                  graphRoleOverrides.length,
                                  'override',
                                )}
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <Button
                          variant={
                            membership.account.sso ? 'hidden' : 'destructive'
                          }
                          isDisabled={!!membership.account.sso}
                          onClick={() => setSelectedOrg(membership)}
                        >
                          {membership.account.sso
                            ? 'Managed externally'
                            : 'Leave organization'}
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {organizations.length === 0 && (
            <div className="flex justify-center py-12 text-base text-secondary">
              <div className="max-w-xl text-center">
                You're not a member of any organizations yet. Create a new
                organization to get started using Studio.
              </div>
            </div>
          )}
        </CardBody>
      </Card>
      {selectedOrg && (
        <Modal isOpen onClose={onModalClose} size="lg">
          <ModalOverlay />
          <Form onSubmit={submit}>
            <ModalContent>
              {isLastAdminInSelectedAccount ? (
                <>
                  <ModalHeader>
                    <ModalTitle>
                      Cannot leave {selectedOrg.account.name}
                    </ModalTitle>
                  </ModalHeader>
                  <ModalBody>
                    You are the only admin in the {selectedOrg.account.name}{' '}
                    organization. Please designate another account admin before
                    doing this.
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      variant="secondary"
                      onClick={onModalClose}
                      type="button"
                    >
                      Cancel
                    </Button>
                  </ModalFooter>
                </>
              ) : (
                <>
                  <ModalHeader>
                    <ModalTitle>
                      Are you sure you want to leave {selectedOrg.account.name}
                    </ModalTitle>
                  </ModalHeader>
                  <ModalBody>
                    <p>
                      You will no longer have access to the{' '}
                      <b>{selectedOrg.account.name}</b> organization and must be
                      re-invited to get access.
                    </p>
                    {!!error && <ErrorMessage>{error}</ErrorMessage>}
                  </ModalBody>
                  <ModalFooter>
                    <ButtonGroup>
                      <Button
                        variant="secondary"
                        onClick={onModalClose}
                        type="button"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        isDisabled={loading}
                        type="submit"
                      >
                        Yes, leave
                      </Button>
                    </ButtonGroup>
                  </ModalFooter>
                </>
              )}
            </ModalContent>
          </Form>
        </Modal>
      )}
    </>
  );
}
